<template>
	<div class="container-fluid mt-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h2>
				<i class="bi bi-crosshair me-2"></i>
				Position Inference Anchoring
			</h2>
			<div class="d-flex gap-2">
				<button
					class="btn btn-outline-primary btn-sm"
					:disabled="loading"
					@click="toggleOutline"
					title="Toggle pool outline overlay (O)"
				>
					<i :class="showOutline ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
					{{ showOutline ? "Hide outline" : "Show outline" }}
				</button>
				<button
					class="btn btn-primary btn-sm"
					:disabled="loading || saving || pool === null"
					@click="commitPool"
					title="Commit the pool's location and mark it reviewed"
				>
					<i class="bi bi-pin-map"></i>
					Commit location
				</button>
				<button
					class="btn btn-outline-primary btn-sm"
					:disabled="loading"
					@click="nextPool"
				>
					<i class="bi bi-arrow-repeat"></i>
					Next pool
				</button>
				<RouterLink
					to="/_/sudo/vision-tasks"
					class="btn btn-outline-secondary btn-sm"
				>
					<i class="bi bi-arrow-left"></i> Back
				</RouterLink>
				<button
					class="btn btn-outline-secondary btn-sm"
					title="Keyboard Shortcuts (?)"
					@click="showShortcutsModal = true"
				>
					<i class="bi bi-keyboard"></i>
				</button>
			</div>
		</div>

		<div v-if="loading" class="text-center text-muted py-5">
			<div class="spinner-border text-primary mb-2" role="status"></div>
			<p class="small">Loading unreviewed pool...</p>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="pool === null" class="text-center text-muted py-5">
			<p>All unreviewed pools have been reviewed.</p>
			<button class="btn btn-outline-primary btn-sm" @click="nextPool">
				<i class="bi bi-arrow-repeat"></i> Refresh
			</button>
		</div>

		<div v-else-if="!pool.tile" class="alert alert-warning">
			Could not compute a centering tile for this pool.
		</div>

		<div v-else>
			<div class="d-flex align-items-center gap-3 mb-3 text-muted">
				<span>
					<RouterLink
						:to="`/_/sudo/vision-tasks/position-inference-anchoring/${pool.id}`"
						class="link-primary text-decoration-none"
						title="Link to this pool"
					>
						Pool #{{ pool.id }}
						<i class="bi bi-box-arrow-up-right"></i>
					</RouterLink>
				</span>
				<span>{{ pool.condition }}</span>
				<span>{{ formatDate(pool.createdAt) }}</span>
				<span>Tile {{ pool.tile.z }}/{{ pool.tile.x }}/{{ pool.tile.y }}</span>
			</div>

			<div :key="pool.id" class="row g-3">
				<div v-for="v in variants" :key="v.id" class="col-md-6">
					<div class="card h-100">
						<div class="card-header py-2">
							<strong>{{ v.label }}</strong>
						</div>
						<div
							class="tile-panel"
							:class="{ 'is-dragging': dragging !== null }"
							@mousedown="onTileMouseDown"
						>
							<img
								:src="tileURL(v.id)"
								alt=""
								class="tile-img"
								loading="lazy"
								@error="onImgError($event, v.id)"
							/>
							<svg
								v-if="showOutline && outlinePoints"
								class="tile-overlay"
								viewBox="0 0 256 256"
								preserveAspectRatio="none"
							>
								<polygon
									:points="outlinePoints"
									fill="none"
									stroke="#ffc107"
									stroke-width="2.5"
								/>
							</svg>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>

	<Teleport to="body">
		<div v-if="showShortcutsModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showShortcutsModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-keyboard"></i>
								Keyboard Shortcuts
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showShortcutsModal = false"
							></button>
						</div>
						<div class="modal-body">
							<table class="table table-sm small mb-0">
								<tbody>
									<tr>
										<td><kbd>O</kbd></td>
										<td>Toggle pool outline overlay</td>
									</tr>
									<tr>
										<td><kbd>N</kbd></td>
										<td>Next pool</td>
									</tr>
									<tr>
										<td><kbd>S</kbd></td>
										<td>Commit location</td>
									</tr>
									<tr>
										<td><kbd>?</kbd></td>
										<td>Show keyboard shortcuts</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
.tile-panel {
	position: relative;
	width: 100%;
	aspect-ratio: 1 / 1;
	background: #e9ecef;
	cursor: grab;
}
.tile-panel.is-dragging {
	cursor: grabbing;
	user-select: none;
}
.tile-img {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: fill;
}
.tile-overlay {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { apiClient } from "@/client";
import ToastNotification from "@/components/ToastNotification.vue";
import type { VisionPositionInferencePool } from "@/type/api";

const route = useRoute();
const router = useRouter();

interface ImageryVariant {
	id: string;
	label: string;
}

const variants: ImageryVariant[] = [
	{ id: "planet", label: "Planet (RGB)" },
	{ id: "blue-excess", label: "Blue Excess" },
	{ id: "ndwi", label: "NDWI" },
	{ id: "nir", label: "NIR" },
];

const TILE_PX = 256;

const loading = ref(false);
const error = ref<string | null>(null);
const pool = ref<VisionPositionInferencePool | null>(null);
const brokenVariants = ref<Set<string>>(new Set());
const showOutline = ref(true);

// Offset (in tile pixel space, 0..256) applied to the pool outline after the
// user drags it to a new location. Stored for the commit step, which will
// translate it into the pool's final geographic position.
const dragOffset = ref({ x: 0, y: 0 });

interface DragState {
	panel: HTMLElement;
	startX: number;
	startY: number;
	originX: number;
	originY: number;
}
const dragging = ref<DragState | null>(null);

const saving = ref(false);
const showShortcutsModal = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 5000);
}

function tileURL(variantId: string): string {
	const t = pool.value?.tile;
	if (!t) return "";
	const baseURLs: Record<string, string> = {
		planet: "/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1",
		"blue-excess": "/api/tile/planet/any/{z}/{x}/{y}?transform=blue-excess",
		ndwi: "/api/tile/planet/any/{z}/{x}/{y}?transform=ndwi",
		nir: "/api/tile/planet/any/{z}/{x}/{y}?band=4&brightness=auto",
	};
	let url = baseURLs[variantId]
		.replace("{z}", String(t.z))
		.replace("{x}", String(t.x))
		.replace("{y}", String(t.y));
	if (pool.value?.itemId) {
		url += `&source=${encodeURIComponent(pool.value.itemId)}`;
	}
	return url;
}

// Project the pool's outer ring into tile pixel space (0..256) so we can draw
// the outline as an SVG over the static tile image.
const outlinePoints = computed(() => {
	const t = pool.value?.tile;
	const geom = pool.value?.geometry as
		| { type: string; coordinates: unknown }
		| null
		| undefined;
	if (!t || !geom) return "";

	const rings = collectRings(geom);
	if (rings.length === 0) return "";
	const ring = rings[0];

	const pts = ring
		.map(([lng, lat]) => {
			const x = ((lng + 180) / 360) * Math.pow(2, t.z);
			const latRad = (lat * Math.PI) / 180;
			const y =
				((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) /
					2) *
				Math.pow(2, t.z);
			return `${((x - t.x) * TILE_PX + dragOffset.value.x).toFixed(1)},${(
				(y - t.y) * TILE_PX +
				dragOffset.value.y
			).toFixed(1)}`;
		})
		.join(" ");
	return pts;
});

function collectRings(geom: {
	type: string;
	coordinates: unknown;
}): [number, number][][] {
	const coords = geom.coordinates as unknown;
	if (geom.type === "Polygon") {
		return (coords as [number, number][][]) ?? [];
	}
	if (geom.type === "MultiPolygon") {
		const polys = coords as [number, number][][][];
		if (!polys || polys.length === 0) return [];
		return polys[0] ?? [];
	}
	return [];
}

function onImgError(e: Event, variantId: string) {
	const target = e.target as HTMLImageElement;
	target.style.visibility = "hidden";
	brokenVariants.value.add(variantId);
}

function onTileMouseDown(e: MouseEvent) {
	if (e.button !== 0) return;
	const panel = e.currentTarget as HTMLElement | null;
	if (!panel) return;
	e.preventDefault();
	dragging.value = {
		panel,
		startX: e.clientX,
		startY: e.clientY,
		originX: dragOffset.value.x,
		originY: dragOffset.value.y,
	};
	window.addEventListener("mousemove", onDragMove);
	window.addEventListener("mouseup", onDragEnd);
}

function onDragMove(e: MouseEvent) {
	const d = dragging.value;
	if (!d) return;
	// The panel renders the 256px tile scaled to its CSS size; convert the
	// mouse delta (CSS px) into tile pixel space so the outline tracks 1:1.
	const rect = d.panel.getBoundingClientRect();
	const scale = TILE_PX / rect.width;
	dragOffset.value = {
		x: d.originX + (e.clientX - d.startX) * scale,
		y: d.originY + (e.clientY - d.startY) * scale,
	};
}

function onDragEnd() {
	dragging.value = null;
	window.removeEventListener("mousemove", onDragMove);
	window.removeEventListener("mouseup", onDragEnd);
}

function formatDate(iso: string): string {
	if (!iso) return "\u2014";
	return new Date(iso).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

const BASE_PATH = "/_/sudo/vision-tasks/position-inference-anchoring";

// The id of the pool currently shown, so the route watcher can distinguish a
// URL change we made ourselves (after a random load) from a real navigation.
const loadedPoolId = ref<string | null>(null);

async function loadPool(forceRandom = false) {
	loading.value = true;
	error.value = null;
	brokenVariants.value = new Set();
	dragOffset.value = { x: 0, y: 0 };
	dragging.value = null;
	try {
		const poolId = forceRandom
			? undefined
			: (route.params.poolId as string | undefined);
		const url = poolId
			? `/api/vision/review/position-inference/${encodeURIComponent(poolId)}`
			: "/api/vision/review/position-inference";
		const result = await apiClient.JSONGet<VisionPositionInferencePool | null>(
			url,
		);
		pool.value = result;
		if (result) {
			loadedPoolId.value = String(result.id);
			// A random load has no poolId in the URL yet — write it so the
			// pool is shareable/bookmarkable.
			if (!poolId) {
				router.replace(`${BASE_PATH}/${result.id}`);
			}
		} else {
			loadedPoolId.value = null;
		}
	} catch (e: unknown) {
		// Axios error or generic Error from the API client — read message if present.
		const err = e as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		error.value =
			err?.response?.data?.message || err?.message || "Failed to load pool.";
		pool.value = null;
		loadedPoolId.value = null;
	} finally {
		loading.value = false;
	}
}

function toggleOutline() {
	showOutline.value = !showOutline.value;
}

function onKeydown(e: KeyboardEvent) {
	// Ignore when focus is in a text input / textarea.
	const target = e.target as HTMLElement | null;
	if (
		target &&
		(target.tagName === "INPUT" ||
			target.tagName === "TEXTAREA" ||
			target.isContentEditable)
	) {
		return;
	}
	// Reserve browser/modifier shortcuts (e.g. Cmd+S) for their normal use.
	if (e.ctrlKey || e.metaKey || e.altKey) return;

	switch (e.key.toLowerCase()) {
		case "o":
			toggleOutline();
			break;
		case "n":
			if (!loading.value) nextPool();
			break;
		case "s":
			if (!loading.value && !saving.value && pool.value) commitPool();
			break;
		case "?":
			e.preventDefault();
			showShortcutsModal.value = true;
			break;
		case "escape":
			if (showShortcutsModal.value) showShortcutsModal.value = false;
			break;
	}
}

function nextPool() {
	// Load a random pool regardless of any poolId currently in the URL.
	loadPool(true);
}

// Reverse the Web-Mercator projection used by outlinePoints: take every
// geometry coordinate, shift it by the drag offset in tile-pixel space, and
// project it back to geographic coordinates. Round-tripping each point
// reproduces exactly what the reviewer saw, so the committed geometry is a
// rigid translation of the original pool.
function translateGeometry(
	geom: GeoJSON.Geometry,
	tile: { z: number; x: number; y: number },
	offset: { x: number; y: number },
): GeoJSON.Geometry {
	const z = tile.z;
	const scale = Math.pow(2, z);
	const shift = (lng: number, lat: number): [number, number] => {
		const x = ((lng + 180) / 360) * scale;
		const latRad = (lat * Math.PI) / 180;
		const y =
			((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
			scale;
		const px = (x - tile.x) * TILE_PX + offset.x;
		const py = (y - tile.y) * TILE_PX + offset.y;
		const x2 = px / TILE_PX + tile.x;
		const y2 = py / TILE_PX + tile.y;
		const lng2 = (x2 / scale) * 360 - 180;
		const t = Math.exp(Math.PI * (1 - (2 * y2) / scale));
		const lat2 = (2 * Math.atan(t) - Math.PI / 2) * (180 / Math.PI);
		return [lng2, lat2];
	};

	const coords = (geom as { type: string; coordinates: unknown }).coordinates;
	let newCoords: unknown;
	if (geom.type === "Polygon") {
		newCoords = (coords as number[][][]).map((ring) =>
			ring.map((p) => shift(p[0], p[1])),
		);
	} else if (geom.type === "MultiPolygon") {
		newCoords = (coords as number[][][][]).map((poly) =>
			poly.map((ring) => ring.map((p) => shift(p[0], p[1]))),
		);
	} else {
		return geom;
	}
	return { ...(geom as object), coordinates: newCoords } as GeoJSON.Geometry;
}

async function commitPool() {
	const p = pool.value;
	const t = p?.tile;
	if (!p || !t) {
		showToast("Cannot commit", "This pool has no tile or geometry to save.");
		return;
	}
	saving.value = true;
	try {
		const geometry = translateGeometry(p.geometry, t, dragOffset.value);
		await apiClient.JSONPut(`/api/vision/train/pool/${p.id}`, { geometry });
		showToast("Saved", "Pool location committed.");
		// The pool is now reviewed, so advance to the next unreviewed pool.
		await nextPool();
	} catch (e: any) {
		showToast(
			"Save failed",
			e?.response?.data?.message || e?.message || "Failed to commit pool.",
		);
	} finally {
		saving.value = false;
	}
}

watch(
	() => route.params.poolId,
	(newId) => {
		const pid = (newId as string | undefined) ?? null;
		// Ignore the URL write we just made after a random load.
		if (pid === loadedPoolId.value) return;
		loadPool();
	},
);

onMounted(() => {
	window.addEventListener("keydown", onKeydown);
	loadPool();
});

onUnmounted(() => {
	window.removeEventListener("keydown", onKeydown);
});
</script>
